import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, X } from 'lucide-react';
import { TaxPlanEstimate } from '../../hooks/useTaxPlan';
import { formatAUD, formatAmount, formatDate, formatExactPrice } from '../../utils/formatters';

function CoinBadge({ image, ticker }: { image?: string; ticker: string }) {
  if (image) {
    return <img src={image} alt={ticker} className="w-7 h-7 rounded-full flex-shrink-0 bg-slate-800" />;
  }
  return (
    <div className="w-7 h-7 rounded-full flex-shrink-0 bg-slate-700 flex items-center justify-center">
      <span className="text-[10px] font-semibold text-slate-300">{ticker.slice(0, 3)}</span>
    </div>
  );
}

function Metric({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p className={`text-sm font-medium tabular-nums truncate ${color ?? 'text-slate-100'}`}>{value}</p>
    </div>
  );
}

interface Props {
  estimate: TaxPlanEstimate;
  onDelete: (id: string) => void;
}

export function TaxPlanEstimateRow({ estimate: e, onDelete }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const gainPositive = e.capitalGain >= 0;
  const proceeds = e.amountToSell * e.estimatedSalePrice;

  const handleDelete = (ev: React.MouseEvent) => {
    ev.stopPropagation();
    if (window.confirm('Delete this estimate?')) onDelete(e.id);
  };

  // Open the coin's detail page (same routing the Holdings table uses). Stop
  // propagation so it doesn't also toggle the row's expand/collapse.
  const goToCoin = (ev: React.MouseEvent | React.KeyboardEvent) => {
    ev.stopPropagation();
    navigate(`/coin/${e.coinId}`, { state: { from: 'tax' } });
  };

  return (
    <div
      className="rounded-xl border border-slate-700/60 transition-colors hover:bg-[rgb(var(--overlay)/0.03)]"
    >
      {/* Summary row */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setOpen((o) => !o); } }}
        aria-expanded={open}
        className="w-full flex items-center gap-4 p-4 text-left cursor-pointer"
      >
        {/* Left — identity (clickable → coin detail) */}
        <div
          role="button"
          tabIndex={0}
          onClick={goToCoin}
          onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); goToCoin(ev); } }}
          title={`View ${e.coinName}`}
          className="group flex items-center gap-3 min-w-0 w-[200px] flex-shrink-0 cursor-pointer"
        >
          <CoinBadge image={e.coinImage} ticker={e.coinTicker} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-100 truncate transition-colors group-hover:text-[#d4b483]">
              {e.coinName} <span className="text-slate-500 font-normal">{e.coinTicker}</span>
            </p>
            <p className="text-xs truncate" style={{ color: '#b89968' }}>{e.scenarioName}</p>
          </div>
        </div>

        {/* Middle — key numbers */}
        <div className="hidden sm:grid grid-cols-4 gap-4 flex-1 min-w-0">
          <Metric label="Amount" value={`${formatAmount(e.amountToSell)} ${e.coinTicker}`} />
          <Metric label="Sale price" value={formatExactPrice(e.estimatedSalePrice, 'AUD')} />
          <Metric
            label="Gain"
            value={formatAUD(e.capitalGain, { sign: true })}
            color={gainPositive ? 'text-emerald-400' : 'text-red-400'}
          />
          <Metric label="CGT" value={formatAUD(e.estimatedCGT)} color="text-slate-100" />
        </div>

        {/* Right — saved date + controls */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
          <span className="hidden md:inline text-[11px] text-slate-500 whitespace-nowrap">
            Saved {formatDate(e.savedAt)}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={handleDelete}
            onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); handleDelete(ev as unknown as React.MouseEvent); } }}
            title="Delete this estimate"
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </span>
          <ChevronDown
            className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {/* Expanded details */}
      <div
        className="grid"
        style={{ gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 220ms ease-in-out' }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-4 pb-4 pt-1 border-t border-slate-700/40">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-3">
              <Metric label="Cost basis (per coin)" value={formatExactPrice(e.costBasis, 'AUD')} />
              <Metric label="Total proceeds" value={formatAUD(proceeds)} />
              <Metric label="Hold duration" value={`${e.holdDurationMonths} month${e.holdDurationMonths !== 1 ? 's' : ''}`} />
              <Metric
                label="CGT discount"
                value={e.cgtDiscountApplied ? '50% (12+ mo)' : 'Not eligible'}
                color={e.cgtDiscountApplied ? 'text-emerald-400' : 'text-slate-400'}
              />
              <Metric label="Tax bracket" value={e.taxBracket} />
              <Metric label="Marginal rate" value={`${(e.taxBracketRate * 100).toFixed(0)}%`} />
              {/* Amount/Sale/Gain/CGT repeated here so the detail view is complete on mobile, where the summary row hides them */}
              <Metric label="Amount" value={`${formatAmount(e.amountToSell)} ${e.coinTicker}`} />
              <Metric
                label="Capital gain"
                value={formatAUD(e.capitalGain, { sign: true })}
                color={gainPositive ? 'text-emerald-400' : 'text-red-400'}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
